import { useCallback, useMemo, useRef } from 'react';
import '../Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, Popup as Pop, Lookup, Item as ItemT, RequiredRule, Form, Toolbar, Scrolling, SearchPanel, Paging, Pager } from 'devextreme-react/data-grid';
import { useApiConfiguration } from '../../../api/api.configuration';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { Item } from 'devextreme-react/form';
import { Tooltip } from 'devextreme-react';
import Button from 'devextreme-react/button';
import notify from 'devextreme/ui/notify';
import { useScreenSize } from '../../../utils/media-query';

export const SamplesTypes = ({ dataMuestras, update }) => {
  const dataRef = useRef<DataGrid>(null);
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const { newSampleType, setSampleType, deleteSampleType } = useApiConfiguration();

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Tm_Id',
      load() {
        return Promise.resolve({
          data: dataMuestras,
        }).catch(() => {
          throw new Error('Error de carga de datos');
        });
      },
      insert: async (values) => {
        const res = await newSampleType({ TipoMuestra: values });
        if (res !== 105) throw new Error('Error insertando el tipo de muestra');
        update(); // refetch: la fila nueva necesita el Id generado por el back
      },
      update: async (key, values) => {
        const res = await setSampleType({ TipoMuestra: values, IdTipoMuestra: key });
        if (res !== 107) throw new Error('Error actualizando el tipo de muestra');
        update(); // refetch (igual que el botón Refrescar) → se ve el cambio sin clic
      },
      remove: async (key) => {
        const res = await deleteSampleType({ IdTipoMuestra: key });
        if (res !== 107) throw new Error('Error eliminando el tipo de muestra');
        // el grid quita la fila localmente, sin recargar
      },
    });
  }, [dataMuestras]);

  const customDataSrc = useMemo(
    () =>
      new DataSource({
        store: customData,
        reshapeOnPush: true,
      }),
    [customData]
  );

  //Renderiza la celda de muestras
  const handleSample = useCallback((cellData) => {
    const { data } = cellData;
    return (
      <div className='tube-container' id={`tmSample${data.Tm_Id}`} key={data.Tm_Id}>
        <div className='test-tube'>
          <div className='liquid' style={{ backgroundColor: data.Tm_Color || '#888' }}></div>
        </div>
      </div>
    );
  }, []);

  const handleRefresh = useCallback(() => {
    update();
  }, [update]);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    // Agregar nueva fila al grid
    dataRef.current?.instance.addRow();

    // Después de un pequeño delay, establecer los valores duplicados
    setTimeout(() => {
      const visibleRows = dataRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        // Establecer valores duplicados (excepto el ID)
        dataRef.current?.instance.cellValue(newRowIndex, 'Tm_Color', rowData.Tm_Color);
        dataRef.current?.instance.cellValue(newRowIndex, 'Tm_Nombre', `${rowData.Tm_Nombre} - Copia`);
        dataRef.current?.instance.cellValue(newRowIndex, 'Tm_Descripcion', rowData.Tm_Descripcion);
        dataRef.current?.instance.cellValue(newRowIndex, 'Tm_Sufijo', rowData.Tm_Sufijo);
        dataRef.current?.instance.cellValue(newRowIndex, 'Tm_TiempoDescarte', rowData.Tm_TiempoDescarte);
        dataRef.current?.instance.cellValue(newRowIndex, 'Tm_Estado', true);
      }
    }, 100);

    notify('📋 Tipo de muestra duplicado. Complete la edición para guardar.', 'info', 3000);
  }, []);

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <DataGrid dataSource={customDataSrc} ref={dataRef} width='100%' height='100%'>
        <SearchPanel visible={true} width={300} placeholder='Buscar tipos de muestra...' />
        <Scrolling useNative={false} showScrollbar="always" />
        <Paging enabled={true} defaultPageSize={25} />
        <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
        <Editing mode={isSmall || isXSmall || isMedium ? 'popup' : 'form'} allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
          <Pop title='Tipo de muestra' showTitle={true} fullScreen={isXSmall || isSmall} width={isMedium ? '70%' : 600} height='auto' />
          <Form colCount={isSmall || isXSmall ? 1 : 2} colCountByScreen={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
            <Item dataField='Tm_Color' editorType='dxColorBox' />
            <Item dataField='Tm_Nombre' />
            <Item dataField='Tm_Descripcion' colSpan={isSmall || isXSmall ? 1 : 2} />
            <Item dataField='Tm_Sufijo' />
            <Item dataField='Tm_TiempoDescarte' />
            <Item dataField='Tm_Estado' editorType='dxCheckBox' colSpan={isSmall || isXSmall ? 1 : 2} />
          </Form>
        </Editing>
        <Toolbar>
          <ItemT name='searchPanel' />
          <ItemT name='addRowButton' />
          <ItemT location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefrecarResultsa' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefrecarResultsa'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </ItemT>
        </Toolbar>
        <Column allowGrouping={false} caption='ID' visible={true} allowEditing={false} dataField='Tm_Id' alignment='center' width={80} sortOrder='desc' />
        <Column allowGrouping={false} caption='COLOR' dataField='Tm_Color' alignment='center' cellRender={handleSample} visible={true} allowEditing={true} width={100} minWidth={80}>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='MUESTRA' visible={true} allowEditing={true} dataField='Tm_Nombre' alignment='center' width={150} minWidth={120}>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='DESCRIPCIÓN' visible={true} allowEditing={true} dataField='Tm_Descripcion' alignment='center' width={200} minWidth={150} />
        <Column allowGrouping={false} caption='SUFIJO' visible={true} allowEditing={true} dataField='Tm_Sufijo' alignment='center' width={100} minWidth={80} />
        <Column dataType='number' allowGrouping={false} caption='TIEMPO DESCARTE' visible={true} allowEditing={true} dataField='Tm_TiempoDescarte' alignment='center' width={150} minWidth={120} />
        <Column dataType='boolean' allowGrouping={false} caption='ESTADO' visible={true} allowEditing={true} dataField='Tm_Estado' alignment='center' width={100} minWidth={80} />
        <Column type='buttons' caption={'ACCIÓN'} width={120} minWidth={100}>
          <Butt name='edit' />
          <Butt name='delete' />
          <Butt icon='copy' hint='Duplicar tipo de muestra' onClick={handleDuplicate} />
        </Column>
      </DataGrid>
    </div>
  );
};
